/**
 * 错误监控核心模块
 */
import { ErrorType, type ErrorReport, type ErrorMonitorConfig } from '../types/error';
import { submitTrack } from './tracking';
import { isTestEnv, getEnvCode } from './env';

class ErrorMonitor {
  private config: ErrorMonitorConfig;
  private errorCount = 0;
  private errorCache = new Set<string>(); // 防重复上报缓存
  private throttleCache = new Map<string, number>(); // 节流缓存

  constructor(config: Partial<ErrorMonitorConfig> = {}) {
    this.config = {
      enableInDev: true,
      enableInTest: true,
      enableInPre: true,
      enableInProd: true,
      maxErrors: 100,
      throttleTime: 5000, // 5秒
      ...config
    };
  }

  /**
   * 判断当前环境是否需要上报
   */
  private shouldReport(): boolean {
    const env = import.meta.env.VITE_APP_ENV;

    if (env === 'development') {
      return this.config.enableInDev;
    }
    if (env === 'pre') {
      return this.config.enableInPre;
    }
    if (env === 'production') {
      return this.config.enableInProd;
    }
    if (isTestEnv()) {
      return this.config.enableInTest;
    }
    return false;
  }

  /**
   * 获取环境标识
   */
  private getEnvironment(): string {
    return getEnvCode();
  }

  /**
   * 生成错误唯一标识
   */
  private generateErrorKey(report: ErrorReport): string {
    return `${report.error_type}_${report.message}_${report.error_url}`;
  }

  /**
   * 检查是否需要节流
   */
  private shouldThrottle(errorKey: string): boolean {
    const now = Date.now();
    const lastTime = this.throttleCache.get(errorKey);

    if (lastTime && now - lastTime < this.config.throttleTime) {
      return true;
    }

    this.throttleCache.set(errorKey, now);
    return false;
  }

  /**
   * 上报错误
   */
  public reportError(report: ErrorReport): void {
    console.log('Error Monitor:', report);
    // 检查环境
    if (!this.shouldReport()) {
      console.warn('Error Monitor:', report);
      return;
    }

    // 检查错误数量限制
    if (this.errorCount >= this.config.maxErrors) {
      console.warn('Error Monitor: Maximum error count reached');
      return;
    }

    // 生成错误标识
    const errorKey = this.generateErrorKey(report);

    // 检查重复上报
    if (this.errorCache.has(errorKey)) {
      return;
    }

    // 检查节流
    if (this.shouldThrottle(errorKey)) {
      return;
    }

    // 添加环境信息
    const finalReport = {
      ...report,
      environment: this.getEnvironment()
    };

    try {
      // 使用现有的submitTrack方法上报，事件名为H5Error
      submitTrack('H5Error', finalReport);

      // 记录已上报
      this.errorCache.add(errorKey);
      this.errorCount++;

      console.log('Error reported:', finalReport);
    } catch (error) {
      console.error('Failed to report error:', error);
    }
  }

  /**
   * 监控JavaScript错误
   */
  public initJavaScriptErrorMonitor(): void {
    // 全局错误监听

    window.addEventListener('error', (event) => {
      // 排除资源加载错误
      if (event.target !== window) {
        return;
      }

      this.reportError({
        error_type: ErrorType.JAVASCRIPT,
        message: event.message || 'Unknown JavaScript error',
        error_url: window.location.href,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        environment: this.getEnvironment(),
        stack: JSON.stringify(event.error?.stack),
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Promise未捕获错误监听
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError({
        error_type: ErrorType.JAVASCRIPT,
        message: `Unhandled Promise Rejection: ${event.reason}`,
        error_url: window.location.href,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        environment: this.getEnvironment(),
        stack: JSON.stringify(event.reason?.stack || event.reason)
      });
    });
  }

  /**
   * 监控Vue组件错误
   */
  public createVueErrorHandler() {
    return (err: unknown, instance: any, info: string) => {
      const error = err instanceof Error ? err : new Error(String(err));

      this.reportError({
        error_type: ErrorType.VUE,
        message: error.message || 'Vue component error',
        error_url: window.location.href,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        environment: this.getEnvironment(),
        stack: JSON.stringify(error.stack),
        component_name: instance?.$options?.name || instance?.$options?._componentTag || 'Unknown'
      });
    };
  }

  /**
   * 监控接口请求错误
   */
  public reportRequestError(error: any, config: any): void {
    const status = error.response?.status;
    const responseData = error.response?.data;

    this.reportError({
      error_type: ErrorType.REQUEST,
      message: error.message || 'Request failed',
      error_url: window.location.href,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      environment: this.getEnvironment(),
      status,
      response: typeof responseData === 'object' ? JSON.stringify(responseData) : String(responseData),
      request_url: config?.url || 'Unknown',
      method: config?.method?.toUpperCase() || 'Unknown'
    });
  }

  /**
   * 清理缓存
   */
  public clearCache(): void {
    this.errorCache.clear();
    this.throttleCache.clear();
    this.errorCount = 0;
  }
}

// 导出单例实例
export const errorMonitor = new ErrorMonitor();
